import { useState, useEffect, useMemo } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { API_ORIGIN } from "@/constants"
import { fetchSignupHoldStatus, fetchSignupPolicy } from "@/lib/signup-policy"

interface EmailVerifyProps extends React.ComponentPropsWithoutRef<"div"> {
  onSuccess?: () => void;
}

function resolveVerificationKey(
  searchParams: URLSearchParams,
  splat: string | undefined,
): string | null {
  const fromQuery = searchParams.get("key")?.trim()
  if (fromQuery) {
    return fromQuery
  }

  const fromPath = splat?.replace(/^\/+/, "").replace(/\/+$/, "").trim()
  if (fromPath) {
    try {
      return decodeURIComponent(fromPath)
    } catch {
      return fromPath
    }
  }

  return null
}

function isAllauthVerifySuccess(status: number, data: Record<string, unknown>): boolean {
  if (status >= 200 && status < 300) {
    return true
  }

  // Allauth returns 401 + login/signup flows after a successful verify (same as password reset).
  if (status === 401 && data.status === 401 && data.data && typeof data.data === "object") {
    const flows = (data.data as { flows?: unknown }).flows
    return Array.isArray(flows) && flows.length > 0
  }

  return false
}

function extractAllauthError(data: Record<string, unknown>): string | null {
  const errors = data.errors
  if (Array.isArray(errors) && errors.length > 0) {
    const first = errors[0] as { message?: string; code?: string }
    if (first.message) {
      return first.message
    }
  }
  if (typeof data.error === "string") {
    return data.error
  }
  if (typeof data.detail === "string") {
    return data.detail
  }
  return null
}

function isInvalidOrExpiredKey(data: Record<string, unknown>): boolean {
  const errors = data.errors
  if (!Array.isArray(errors)) {
    return false
  }
  return errors.some((entry) => {
    if (!entry || typeof entry !== "object") {
      return false
    }
    return (entry as { code?: string }).code === "invalid_or_expired_key"
  })
}

/** Classic allauth confirm handles already-verified addresses; headless POST does not. */
async function confirmEmailViaClassicView(key: string): Promise<boolean> {
  const response = await fetch(
    `${API_ORIGIN}/accounts/confirm-email/${encodeURIComponent(key)}/`,
    {
      method: "GET",
      credentials: "include",
      redirect: "manual",
    },
  )
  return response.status === 200 || response.status === 302
}

async function verifyEmailKey(
  key: string,
): Promise<{ ok: true; email?: string } | { ok: false; message: string }> {
  const response = await fetch(`${API_ORIGIN}/_allauth/app/v1/auth/email/verify`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify({ key }),
  })

  const data = (await response.json().catch(() => ({}))) as Record<string, unknown>

  const userEmail =
    data.data && typeof data.data === "object"
      ? (data.data as { user?: { email?: string } }).user?.email
      : undefined

  if (isAllauthVerifySuccess(response.status, data)) {
    return { ok: true, email: userEmail }
  }

  if (isInvalidOrExpiredKey(data) && (await confirmEmailViaClassicView(key))) {
    return { ok: true, email: userEmail }
  }

  return {
    ok: false,
    message:
      extractAllauthError(data)
      ?? "Email verification failed. The link may be invalid or expired.",
  }
}

export function EmailVerify({ className, onSuccess, ...props }: EmailVerifyProps) {
  const { "*": splat } = useParams<{ "*": string }>()
  const [searchParams] = useSearchParams()
  const key = useMemo(
    () => resolveVerificationKey(searchParams, splat),
    [searchParams, splat],
  )
  const [status, setStatus] = useState<'loading' | 'error'>('loading')
  const [message, setMessage] = useState("")
  const navigate = useNavigate()

  useEffect(() => {
    if (!key) {
      setStatus('error')
      setMessage("Invalid verification link")
      return
    }

    const verifyEmail = async () => {
      try {
        const result = await verifyEmailKey(key)
        if (result.ok) {
          const email = searchParams.get("email")?.trim() || result.email
          const hold = email ? await fetchSignupHoldStatus(email) : null
          if (hold?.approval_pending) {
            navigate("/register/pending", { replace: true })
          } else if (!email) {
            const policy = await fetchSignupPolicy().catch(() => null)
            if (policy?.require_approval) {
              navigate("/register/pending", { replace: true })
            } else {
              navigate("/login", {
                replace: true,
                state: { message: "Email verified successfully! Please sign in." },
              })
            }
          } else {
            navigate("/login", {
              replace: true,
              state: { message: "Email verified successfully! Please sign in." },
            })
          }
          onSuccess?.()
          return
        }

        setStatus("error")
        setMessage(result.message)
      } catch (error) {
        console.error("Email verification error:", error)
        setStatus("error")
        setMessage("Network error. Please try again.")
      }
    }

    verifyEmail()
  }, [key, navigate, onSuccess])

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      {status === 'loading' && (
        <div className="text-center">
          <div className="text-lg font-semibold mb-2">Verifying your email...</div>
          <div className="text-sm text-muted-foreground">Please wait</div>
        </div>
      )}
      {status === 'error' && (
        <div className="text-center">
          <div className="p-4 text-lg font-semibold text-red-600 bg-red-50 border border-red-200 rounded-md dark:bg-red-900/20 dark:text-red-400 dark:border-red-800 mb-4">
            {message}
          </div>
          <div className="flex flex-col gap-2">
            <Button
              onClick={() => navigate('/login')}
              className="w-full"
            >
              Go to sign in
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/register')}
              className="w-full"
            >
              Register New Account
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
